import { useEffect, useState, useMemo } from "react";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { currency, fmtDate } from "@/lib/format";
import {
  Building2, Stethoscope, Package, Layers, MapPin, BadgeCheck,
  Clock, Star, Mail, Phone, User,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "hospitals",  label: "Hospitals",   icon: Building2 },
  { id: "doctors",    label: "Doctors",     icon: Stethoscope },
  { id: "packages",   label: "Packages",    icon: Package },
  { id: "departments", label: "Departments", icon: Layers },
];

export default function ProviderNetwork() {
  const [hospitals, setHospitals] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [tab, setTab] = useState("hospitals");
  const [loading, setLoading] = useState(true);
  const [filterHospital, setFilterHospital] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const [h, d] = await Promise.all([api.get("/hospitals"), api.get("/doctors")]);
        setHospitals(h.data); setDoctors(d.data);
      } catch (e) { toast.error(getApiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const allDepartments = useMemo(() => {
    const map = {};
    hospitals.forEach((h) => {
      (h.departments || []).forEach((d) => {
        if (!map[d]) map[d] = [];
        map[d].push(h);
      });
    });
    return map;
  }, [hospitals]);

  const allPackages = useMemo(() => {
    const list = [];
    hospitals.forEach((h) => {
      (h.packages || []).forEach((p) => list.push({ ...p, hospital: h }));
    });
    if (filterHospital !== "all") return list.filter((p) => p.hospital.id === filterHospital);
    return list;
  }, [hospitals, filterHospital]);

  return (
    <div data-testid="provider-network-page">
      <PageHeader
        eyebrow="Network"
        title="Provider Network"
        subtitle="Hospitals, doctors, treatment packages and capabilities across the AIMS network."
      />

      <div className="flex gap-2 border-b border-[#E8E6E1] mb-6 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`provider-tab-${id}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${tab === id ? "border-[#2A4D3E] text-[#2A4D3E]" : "border-transparent text-[#5C6660] hover:text-[#2A332C]"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : (
        <>
          {tab === "hospitals" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="hospitals-grid">
              {hospitals.map((h) => (
                <div key={h.id} className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden hover:shadow-md transition" data-testid={`hospital-${h.id}`}>
                  <div className="h-40 relative overflow-hidden">
                    {h.image_url && <img src={h.image_url} alt="" className="w-full h-full object-cover" />}
                    {h.preferred_provider && (
                      <div className="absolute top-3 right-3 bg-[#D97757] text-white text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                        <Star size={11} fill="white" /> Preferred
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="font-heading font-semibold text-lg text-[#2A332C]">{h.name}</div>
                    <div className="text-xs text-[#5C6660] mt-1 flex items-center gap-1"><MapPin size={11} /> {h.city}, {h.country}</div>
                    <p className="text-sm text-[#5C6660] mt-3 line-clamp-2">{h.description}</p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(h.accreditations || []).map((a) => (
                        <span key={a} className="text-[10px] bg-[#E6F0EB] text-[#2A4D3E] px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                          <BadgeCheck size={10} /> {a}
                        </span>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#E8E6E1]">
                      <Stat label="Departments" value={(h.departments || []).length} />
                      <Stat label="Packages" value={(h.packages || []).length} />
                      <Stat label="TAT" value={`${h.turnaround_time_days || 0}d`} icon={Clock} />
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#E8E6E1] space-y-1 text-xs text-[#5C6660]">
                      {h.international_desk_contact && <div className="flex items-center gap-1.5"><User size={11} /> {h.international_desk_contact}</div>}
                      {h.contact_email && <div className="flex items-center gap-1.5"><Mail size={11} /> {h.contact_email}</div>}
                      {h.contact_phone && <div className="flex items-center gap-1.5"><Phone size={11} /> {h.contact_phone}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "doctors" && (
            <>
              <div className="flex gap-2 mb-4 flex-wrap">
                <button onClick={() => setFilterHospital("all")} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filterHospital === "all" ? "border-[#2A4D3E] bg-[#E6F0EB] text-[#2A4D3E]" : "border-[#E8E6E1] text-[#5C6660]"}`}>
                  All hospitals
                </button>
                {hospitals.map((h) => (
                  <button key={h.id} onClick={() => setFilterHospital(h.id)} data-testid={`filter-hospital-${h.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filterHospital === h.id ? "border-[#2A4D3E] bg-[#E6F0EB] text-[#2A4D3E]" : "border-[#E8E6E1] text-[#5C6660]"}`}>
                    {h.name}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="doctors-grid">
                {doctors
                  .filter((d) => filterHospital === "all" || d.hospital_id === filterHospital)
                  .map((d) => (
                  <div key={d.id} className="bg-white border border-[#E8E6E1] rounded-2xl p-5 hover:shadow-md transition" data-testid={`doctor-${d.id}`}>
                    <div className="flex items-start gap-3">
                      {d.image_url ? (
                        <img src={d.image_url} alt="" className="w-14 h-14 rounded-2xl object-cover" />
                      ) : (
                        <div className="w-14 h-14 rounded-2xl bg-[#E6F0EB] flex items-center justify-center font-semibold text-[#2A4D3E]">
                          {d.name?.[0]}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-[#2A332C]">{d.name}</div>
                        <div className="text-xs text-[#2A4D3E] font-semibold mt-0.5">{d.specialty}</div>
                        <div className="text-[11px] text-[#8E9992] truncate">{hospitals.find((h) => h.id === d.hospital_id)?.name}</div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-[#5C6660]">{d.qualifications}</div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8E6E1] text-[11px] text-[#8E9992]">
                      <span>{d.years_experience}+ years exp</span>
                      <span>{(d.languages || []).join(" · ")}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "packages" && (
            <>
              <div className="flex gap-2 mb-4 flex-wrap">
                <button onClick={() => setFilterHospital("all")} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filterHospital === "all" ? "border-[#2A4D3E] bg-[#E6F0EB] text-[#2A4D3E]" : "border-[#E8E6E1] text-[#5C6660]"}`}>
                  All hospitals
                </button>
                {hospitals.map((h) => (
                  <button key={h.id} onClick={() => setFilterHospital(h.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filterHospital === h.id ? "border-[#2A4D3E] bg-[#E6F0EB] text-[#2A4D3E]" : "border-[#E8E6E1] text-[#5C6660]"}`}>
                    {h.name}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="packages-grid">
                {allPackages.map((p, i) => (
                  <div key={i} className="bg-white border border-[#E8E6E1] rounded-2xl p-5 hover:shadow-md transition" data-testid={`package-${i}`}>
                    <div className="w-10 h-10 rounded-xl bg-[#FBEDE5] text-[#D97757] flex items-center justify-center mb-3">
                      <Package size={17} strokeWidth={1.6} />
                    </div>
                    <div className="font-heading font-semibold text-[#2A332C]">{p.name}</div>
                    <div className="text-xs text-[#8E9992] mt-0.5">{p.hospital.name}</div>
                    <div className="mt-3 text-2xl font-heading font-semibold text-[#2A4D3E]">{currency(p.price_usd)}</div>
                    <p className="text-xs text-[#5C6660] mt-2 line-clamp-2">{p.inclusions || "—"}</p>
                    <div className="mt-3 pt-3 border-t border-[#E8E6E1] flex items-center justify-between text-[11px] text-[#8E9992]">
                      <span>Duration: {p.duration_days} days</span>
                      <span className="flex items-center gap-1"><Clock size={11} /> TAT {p.hospital.turnaround_time_days}d</span>
                    </div>
                  </div>
                ))}
                {allPackages.length === 0 && <div className="col-span-full text-center text-sm text-[#8E9992] py-12">No packages found.</div>}
              </div>
            </>
          )}

          {tab === "departments" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="departments-grid">
              {Object.entries(allDepartments).map(([dept, hosps]) => (
                <div key={dept} className="bg-white border border-[#E8E6E1] rounded-2xl p-5" data-testid={`dept-${dept}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#EAF2F8] text-[#2980B9] flex items-center justify-center mb-3">
                    <Layers size={17} strokeWidth={1.6} />
                  </div>
                  <div className="font-heading font-semibold text-[#2A332C]">{dept}</div>
                  <div className="text-xs text-[#8E9992] mt-0.5">Available at {hosps.length} hospital{hosps.length > 1 ? "s" : ""}</div>
                  <div className="mt-3 space-y-1">
                    {hosps.map((h) => (
                      <div key={h.id} className="text-xs text-[#5C6660] flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-[#2A4D3E]" /> {h.name}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#8E9992] font-semibold">{label}</div>
      <div className="text-sm font-semibold text-[#2A332C] flex items-center gap-1">
        {Icon && <Icon size={11} />} {value}
      </div>
    </div>
  );
}
